import { useEffect, useRef, useState } from 'react';

/**
 * Keep an overlay mounted long enough to animate out.
 *
 * `Dialog` and `SidePanel` unmounted the instant `isOpen` went false, so there was
 * nothing left on screen to animate — entrances were added in § 12 but exits stayed
 * instant. This defers the unmount by the exit duration and reports which phase the
 * overlay is in, so the component can swap its entrance class for its exit class.
 *
 * **The failure mode this is built around: `animationend` is not reliable here.**
 * The exit animations are behind `motion-safe:`, so under
 * `prefers-reduced-motion: reduce` **no animation runs at all** and `animationend`
 * never fires. An implementation that waited for that event would leave the dialog
 * mounted forever — a modal that cannot be closed, for exactly the users least able
 * to work around it. So the unmount is driven by a timer, and reduced-motion
 * unmounts immediately.
 *
 * The duration comes from `MOTION` in `./motion.ts`, which a test pins against the
 * durations in `tailwind.config.js` so the timer cannot drift from the CSS.
 */

export type PresencePhase = 'entering' | 'exiting';

const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export function usePresence(
  isOpen: boolean,
  exitMs: number,
): { isPresent: boolean; phase: PresencePhase } {
  const [isPresent, setIsPresent] = useState(isOpen);
  const [phase, setPhase] = useState<PresencePhase>(isOpen ? 'entering' : 'exiting');
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }

    if (isOpen) {
      setIsPresent(true);
      setPhase('entering');
      return undefined;
    }

    // Closing. Nothing to wait for if it was never on screen.
    setPhase('exiting');
    if (prefersReducedMotion()) {
      setIsPresent(false);
      return undefined;
    }

    timer.current = setTimeout(() => {
      timer.current = null;
      setIsPresent(false);
    }, exitMs);

    return () => {
      if (timer.current) {
        clearTimeout(timer.current);
        timer.current = null;
      }
    };
  }, [isOpen, exitMs]);

  return { isPresent, phase };
}
